import * as dom from 'OK/utils/dom';
import throttle from 'OK/utils/throttle';
import * as utils from 'OK/utils/utils';
import { isWheelEventPassiveEnabled } from 'OK/utils/wheelEventOptions';

/**
 * Залипающий блок, который умеет еще и скроллиться
 *
 * Текущее применение — четвертая колонка. Кейсы к проверке:
 * 1) Корректное «залипание», если высота плашки меньше вьюпорта (залипает вверх)
 * 2) Корректное «залипание», если высота плашки больше вьюпорта (скроллится и залипает по обеим сторонам)
 * 3) Ни при каких условиях не выезжает за вернюю границу своей обертки
 * 4) Должен корректно отрабатывать переход из 3х колонок в 4 и обратно; корректной считается отрисовка во вьюпорте, а не вовне
 * 5) Должен корректно обрабатываться пересчет верхней границы обертки, позиция должна компенсироваться при необходимости
 * 6) Должен корректно обрабатываться при ненулевой начальной позиции скролла
 */

enum State {
    TOP = 'top',
    SCROLL = 'scroll',
    BOTTOM = 'bottom',
    HIDE = 'hide'
}

enum ScrollDirection {
    UP = 'up',
    DOWN = 'down'
}

const THROTTLE_DELAY = 10;
const hooks: Record<string, StickyBlockConstructor> = {};
const toolbarHeight = OK.topPanelHeight();
const isMainContentHideEnabled = utils.gwtExperimentsCfg.mch;

/* Вспомогательные функции */

/**
 * Получить абсолютное расположение по вертикали относительно окна
 */
function getAbsoluteTopPosition(node: HTMLElement): number {
    return node.getBoundingClientRect().top + window.pageYOffset;
}

/**
 *
 * @param {HTMLElement} hook
 * @constructor
 */
export class StickyBlockConstructor {
    uselessContentCompensation = 0;
    useHideInsteadBottom: boolean;
    uselessContent?: HTMLElement;
    uselessContentSpacer?: HTMLElement;
    fixedTopThreshold: number;
    wrapperAbsoluteTop: number;
    useMouseWheel: boolean;
    // Сдвиг контейнера относительно обертки
    topShift = 0;
    stickyWrapper: HTMLElement;
    stickyContainer: HTMLElement;
    scrollContainer: HTMLElement;
    mainContentColumn: HTMLElement;

    scrollDelta!: number;
    scrollPosition!: number;
    scrollDirection: ScrollDirection | null = null;
    state: State | null = null;

    isWheelEventPassive: boolean = false;
    wheelEventOptions: AddEventListenerOptions = { capture: false, passive: true };

    scrollHandler: () => void;
    resizeHandler: () => void;

    constructor(el: HTMLElement) {
        this.useHideInsteadBottom = el.getAttribute('data-use-hide') === '1';
        if (el.getAttribute('data-useless-content')) {
            // неважный контент в залипающей колонке, который можно не доскролливать при залипании (аватарка над главным меню)
            this.uselessContent = el.querySelector(el.getAttribute('data-useless-content')!)!;

            if (this.uselessContent) {
                this.uselessContentCompensation = this.uselessContent.offsetHeight;
                // пока контент не важен, мы его подменяем на пустой div
                // это позволит избежать визуального шума, когда по скроллу
                // вверх левая колонка не успела залипнуть и вылезла аватарка
                // + оптимизация, над аватаркой не будут случайно крутиться подарки
                this.uselessContentSpacer = document.createElement('div');
                this.uselessContentSpacer.style.height = this.uselessContentCompensation + 'px';
                this.uselessContentSpacer.classList.add('invisible');
                this.uselessContent.parentElement?.insertBefore(this.uselessContentSpacer, this.uselessContent);
            }
        }
        this.fixedTopThreshold = toolbarHeight - this.uselessContentCompensation;
        if (getHookId(el) === 'rightColumn') {
            this.fixedTopThreshold += 12;
        }
        this.wrapperAbsoluteTop = 0;
        this.useMouseWheel = el.getAttribute('data-mouse-wheel') === '1';

        this.stickyWrapper = el;
        // @ts-ignore
        this.stickyContainer = document.querySelector<HTMLElement>(el.getAttribute('data-sticky-container')) || el.firstChild;
        // @ts-ignore
        this.scrollContainer = document.querySelector<HTMLElement>(el.getAttribute('data-scroll-container')) || window;
        this.mainContentColumn = document.querySelector<HTMLElement>(el.getAttribute('data-main-content-column')!)!;

        this.scrollHandler = throttle(THROTTLE_DELAY, this.onScroll.bind(this));
        this.resizeHandler = throttle(THROTTLE_DELAY, this.onResize.bind(this));

        this.init(true);
        this.render();

        this.scrollContainer.addEventListener('scroll', this.scrollHandler, false);
        if (this.useMouseWheel) {
            this.isWheelEventPassive = isWheelEventPassiveEnabled() && dom.isEventListenerOptionsSupported();
            this.scrollContainer.addEventListener('wheel', this.scrollHandler, this.isWheelEventPassive ? this.wheelEventOptions : false);
        }
        window.addEventListener('resize', this.resizeHandler, false);
    }

    /**
     * Пересчитывает абсолютные значения отступов обертки и контейнера
     * Вызывать при изменении геометрии смежных блоков (например, размеров баннера)
     * @param {boolean} reset принудительный сброс отступов
     */
    init(reset: boolean) {
        const wrapperAbsoluteTop = getAbsoluteTopPosition(this.stickyWrapper);
        this.scrollDelta = 0;
        this.scrollPosition = window.pageYOffset;
        this.scrollDirection = null;

        // Магическое число, равное расстоянию между тулбаром и островом без прокрутки
        // Т.е. во всех случаях, когда тулбар не наезжает на остров, сбрасывать позицию
        // this.wrapperAbsoluteTop - this.fixedTopThreshold
        if (this.scrollPosition < 150) {
            reset = true;
        }

        if (reset) {
            this.setTopShift(0);
            this.setState(State.SCROLL);
            if (this.uselessContent) {
                this.uselessContent.classList.remove('invisible');
                this.uselessContentSpacer?.classList.add('invisible');
            }
        } else {

            // 1. Перерасчет должен выполняться, только если блок уже был инициализирован ранее (this.wrapperAbsoluteTop !== 0)
            // 2. Перерасчет не должен выполняться, если сдвига this.topShift нет
            // 3. С состояниями, отличными от states.SCROLL, рендер разберется сам
            if (this.wrapperAbsoluteTop && this.topShift && (this.state === State.SCROLL)) {
                // Компенсируем сдвиг разницей между старым положением обертки и новым
                const topShift = this.topShift + (this.wrapperAbsoluteTop - wrapperAbsoluteTop);
                this.setTopShift(topShift);
                this.setState(State.SCROLL);
            }
        }

        if (this.state === State.SCROLL) {
            this.wrapperAbsoluteTop = wrapperAbsoluteTop;
        }
    }

    setState(state: State) {
        const stickyContainerClassList = this.stickyContainer.classList;
        let hideUseLessContent = false;
        switch (state) {
            case State.TOP:
                stickyContainerClassList.remove('__fixed-bottom', '__fixed-hide');
                stickyContainerClassList.add('__fixed-top');
                this.stickyContainer.style.marginTop = (-this.uselessContentCompensation) + 'px';
                hideUseLessContent = true;
                break;
            case State.SCROLL:
                stickyContainerClassList.remove('__fixed-top', '__fixed-bottom', '__fixed-hide');
                this.stickyContainer.style.marginTop = this.topShift + 'px';
                break;
            case State.BOTTOM:
                stickyContainerClassList.remove('__fixed-top');
                stickyContainerClassList.add('__fixed-bottom');
                this.stickyContainer.style.marginTop = '0';
                hideUseLessContent = true;
                break;
            case State.HIDE:
                stickyContainerClassList.remove('__fixed-top');
                stickyContainerClassList.add('__fixed-hide');
                this.stickyContainer.style.marginTop = '0';
                hideUseLessContent = true;
                break;
        }

        if (hideUseLessContent && this.uselessContent) {
            this.uselessContent.classList.add('invisible');
            this.uselessContentSpacer?.classList.remove('invisible');
        }

        this.state = state;
    }

    setTopShift(value: number) {
        if (this.topShift !== value) {
            this.topShift = value < 0
                ? 0
                : value;
        }
    }

    render() {

        const scrollPosition = window.pageYOffset;
        const viewportHeightWithoutToolbar = window.innerHeight - this.fixedTopThreshold;

        const rectObject = this.stickyContainer.getBoundingClientRect();
        const stickyHeight = rectObject.height;
        const wrapperAbsoluteTop = getAbsoluteTopPosition(this.stickyWrapper);

        // Панель определяет скролл всего элемента, менять состояние нельзя во избежание скачков
        if ((!(isMainContentHideEnabled && OK.Layers.isAnyLayerOpened)) && (document.documentElement.scrollHeight - document.body.clientHeight) < scrollPosition) {
            return;
        }

        // Панель не меньше высоты документа
        if (document.documentElement.scrollHeight <= (wrapperAbsoluteTop + stickyHeight)) {
            this.init(true);
            return;
        }

        if (this.mainContentColumn) {
            // если залипающий блок выше основного контента, то и залипать не надо. 12px - нижний отступ
            if (this.stickyWrapper.clientHeight > (this.mainContentColumn.clientHeight + 12)) {
                this.init(true);
                return;
            }
        }

        if (!this.useHideInsteadBottom && stickyHeight <= viewportHeightWithoutToolbar) {
            // Стики-панель меньше вьюпорта
            if (scrollPosition > (wrapperAbsoluteTop - this.fixedTopThreshold)) {
                this.setState(State.TOP);
            } else {
                this.init(true);
            }
        } else {
            // Стики-панель больше вьюпорта

            if (this.scrollDirection === ScrollDirection.UP) {
                // не выше, чем обертка
                if ((wrapperAbsoluteTop - this.fixedTopThreshold) > scrollPosition) {
                    this.init(true);
                    return;
                }

                switch (this.state) {
                    case (State.SCROLL):
                        if (rectObject.top > this.fixedTopThreshold) {
                            this.setState(State.TOP);
                        }
                        break;
                    case (State.BOTTOM):
                    case (State.HIDE):
                        // this.scrollDelta - учитываем, что скролл уже произошел
                        this.setTopShift(scrollPosition + rectObject.top - wrapperAbsoluteTop - this.scrollDelta);
                        this.setState(State.SCROLL);
                        break;
                }
            }

            if (this.scrollDirection === ScrollDirection.DOWN) {

                switch (this.state) {
                    case (State.TOP):
                        this.setTopShift(scrollPosition + rectObject.top - wrapperAbsoluteTop);
                        this.setState(State.SCROLL);
                        break;
                    case (State.SCROLL):
                        // не ниже нижней границы вьюпорта
                        if (this.useHideInsteadBottom) {
                            if (rectObject.bottom <= 0) {
                                this.setState(State.HIDE);
                            }
                        } else {
                            if (rectObject.bottom <= window.innerHeight) {
                                this.setState(State.BOTTOM);
                            }
                        }
                        break;
                }
            }

            // Загрузка произошла, позиция скролла фактически ненулевая, а события на скролл не случилось
            // (Хром пытается сохранить предыдущую позицию скролла, если загрузка достаточно быстра)
            if (this.scrollDirection === null && this.topShift === 0) {
                if (scrollPosition > (wrapperAbsoluteTop - this.fixedTopThreshold)) {
                    this.setState(State.TOP);
                }
            }
        }

    }

    destroy() {
        this.scrollContainer.removeEventListener('scroll', this.scrollHandler, false);
        if (this.useMouseWheel) {
            this.scrollContainer.removeEventListener('wheel', this.scrollHandler, this.isWheelEventPassive ? this.wheelEventOptions : false);
        }
        window.removeEventListener('resize', this.resizeHandler);
    }

    onScroll() {
        const position = window.pageYOffset;
        if (position === this.scrollPosition) {
            return;
        }

        this.scrollDelta = position - this.scrollPosition;
        this.scrollDirection = this.scrollDelta > 0
            ? ScrollDirection.DOWN
            : ScrollDirection.UP;

        this.scrollPosition = position;

        this.render();
    }

    onResize() {
        this.render();
    }
}

/* Вспомогательные функции для активации блоков */

/**
 * Получить id хука для учета всех активированных
 */
function getHookId(el: HTMLElement): string | null {
    return el.getAttribute("data-sticky-id");
}

export function activate(el: HTMLElement) {
    const hookId = getHookId(el);
    if (!hookId) {
        throw new Error("data-sticky-id must be specified");
    }
    const instance = new StickyBlockConstructor(el);
    hooks[hookId] = instance;
    return instance;
}

export function deactivate(el: HTMLElement) {
    const hookId = getHookId(el);
    if (hookId) {
        hooks[hookId].destroy();
        delete hooks[hookId];
    }
}
